/**
 * one-stop/strings-eu — the German, French and Italian chrome catalogs.
 *
 * WHY A SIBLING FILE. `strings.ts` holds the resolver, the plural rule and the
 * `en-US` catalog that DEFINES `StringKey`; four more full catalogs in the same
 * file would put ~900 lines of translation between a reader and 40 lines of
 * logic. The catalogs are data; the resolution is code. `strings.ts` imports
 * these and registers them, so the CATALOG map stays the one place a locale is
 * declared.
 *
 * `de-CH` IS LAYERED OVER `de` (JAI-1154, the shape CC's own server table uses):
 * one entry with the Swiss deltas only — «…» in place of „…“, and no `ß` — and
 * everything else inherits through the resolver's base-language step. That is
 * why `DE_CH` is short by design and not a partially translated catalog: no key
 * can fall through to English while `de` carries it.
 *
 * FORMAL REGISTER THROUGHOUT (Sie / vous / Lei). These pages are a bank's and a
 * public authority's front door; the informal register is a tenant setting
 * (`courtesy_form`), not the renderer's to choose.
 *
 * ui-config: closed UI vocabulary. No persona, wait time, department or event.
 */
import type { StringKey } from "./strings";

export type LocaleCatalog = Partial<Record<StringKey, string>>;

/** ui-config — de. Quotes „…“; ß where standard German uses it. */
export const DE: LocaleCatalog = {
  "page.unavailable": "Diese Seite ist gerade nicht verfügbar.",
  "page.unavailable.body": "Bitte versuchen Sie es in einem Moment erneut.",

  "search.placeholder": "Sagen Sie es in Ihren eigenen Worten…",
  "search.findHelp": "Hilfe finden",
  "search.clear": "Suche zurücksetzen",
  "search.clearShort": "Zurücksetzen",
  "search.aria": "Nach Hilfe suchen",
  "search.results.one": "{n} Ergebnis für „{q}“",
  "search.results.other": "{n} Ergebnisse für „{q}“",
  "search.results.sub": "Kommen Sie ohne Termin vorbei oder buchen Sie eine Zeit — was schneller geht.",
  "search.noMatch.title": "Nichts passt zu „{q}“",
  "search.noMatch.body": "Versuchen Sie die Wörter aus Ihren Unterlagen, oder starten Sie hier:",
  "search.error": "Die Suche ist gerade nicht verfügbar.",

  "busy.short-wait": "Kurze Wartezeit",
  "busy.busy": "Viel Betrieb",
  "busy.very-busy": "Lange Wartezeit",
  "busy.closed": "Geschlossen",

  "queues.title": "Jetzt offen",
  "queues.subtitle": "Ohne Termin vorbeikommen — das sind die aktuellen Wartezeiten.",
  "queues.empty": "Aktuelle Wartezeiten sind gerade nicht verfügbar.",
  "queues.join": "In die Warteschlange",
  "queues.book": "Zeit buchen",
  "queues.minutes": "Min.",
  "queues.hours": "Std.",
  "queues.walk": "{n} Min. zu Fuß",
  "queues.openUntil": "Offen bis {t}",
  "queues.ahead": "{n} vor Ihnen",
  "queues.more.one": "{n} weiterer Schalter im Bereich.",
  "queues.more.other": "{n} weitere Schalter im Bereich.",
  "queues.sort.shortest-wait": "Kürzeste Wartezeit zuerst",
  "queues.sort.name": "Nach Name",
  "queues.sort.location": "Nach Standort",
  "queues.sort.curated": "In der hier festgelegten Reihenfolge",

  "appointments.title": "Lieber nicht warten? Zeit buchen",
  "appointments.subtitle": "Die nächsten freien Zeiten in dieser Woche.",
  "appointments.empty": "Gerade sind keine buchbaren Zeiten verfügbar.",
  "appointments.cta": "Buchen",
  "appointments.none": "In diesem Zeitraum nichts frei",
  "appointments.more.one": "{n} weitere Dienstleistung im Bereich.",
  "appointments.more.other": "{n} weitere Dienstleistungen im Bereich.",

  "events.title": "Diese Woche",
  "events.subtitle": "Workshops, Messen und offene Sprechstunden.",
  "events.empty": "In diesem Zeitraum ist nichts geplant.",
  "events.featured.eyebrow": "Empfohlen",
  "events.featured.title": "Das sollten Sie nicht verpassen",
  "events.featured.empty": "Zurzeit ist keine Veranstaltung hervorgehoben.",
  "events.all": "Alle Veranstaltungen",
  "events.cta": "Anmelden",
  "events.more.one": "{n} weitere Veranstaltung im Bereich.",
  "events.more.other": "{n} weitere Veranstaltungen im Bereich.",

  "reg.open": "Anmeldung offen",
  "reg.closed": "Anmeldung geschlossen",
  "reg.full": "Ausgebucht",
  "reg.waitlist": "Warteliste",
  "reg.waitlist.count": "Warteliste — {n} wartend",
  "reg.not-required": "Keine Anmeldung nötig",
  "reg.counts": "{label} ({a}/{b})",
  "reg.countsOnly": "{label} ({a} angemeldet)",

  "directory.title": "Abteilungen ansehen",
  "directory.titleCount": "Alle {n} Abteilungen ansehen",
  "directory.subtitle":
    "Wenn Sie schon wissen, wen Sie brauchen — jede Abteilung, was sie anbietet und wie.",
  "directory.empty": "Die Abteilungsliste ist gerade nicht verfügbar.",
  "directory.show": "Anzeigen",
  "directory.hide": "Ausblenden",
  "directory.inside.one": "{n} darin",
  "directory.inside.other": "{n} darin",
  "directory.cap.walkIns": "Ohne Termin",
  "directory.cap.books": "Buchen",
  "directory.cap.events": "Veranstaltungen",
  "directory.more": "{n} weitere in diesem Teil der Organisation.",

  "checklist.title": "Diese Woche",
  "checklist.empty": "Diese Woche ist für Sie alles erledigt.",
  "checklist.more": "Alle {n} anzeigen",
  "checklist.less": "Weniger anzeigen",
  "checklist.cta.join-queue": "Ohne Termin",
  "checklist.cta.book-appointment": "Buchen",
  "checklist.cta.register-event": "Anmelden",

  "identity.eyebrow": "Jetzt gerade",
  "identity.signedOut.title": "Melden Sie sich für Ihr Konto an",
  "identity.signedOut.button": "Weiter zur Anmeldung",
  "identity.findVisit": "Schon in einer Warteschlange? Besuch finden →",
  "identity.greeting": "Willkommen zurück, {name}",
  "identity.greetingGeneric": "Willkommen zurück",
  "identity.ahead": "vor Ihnen",
  "identity.ticketCta": "Mein Ticket ansehen",
  "identity.visitsCta": "Meine Besuche",
  "identity.degraded": "Einige Angaben sind gerade nicht verfügbar.",
  "identity.nextAppointment": "Nächster Termin",
  "identity.ticket.waiting": "In der Warteschlange bei {queue}",
  "identity.ticket.called": "Sie wurden bei {queue} aufgerufen",
  "identity.ticket.being-served": "Sie werden bei {queue} bedient",
  "identity.signOut": "Abmelden",
  "identity.demo": "Demo",
  "identity.sessionEnded": "Ihre Sitzung ist beendet. Bitte melden Sie sich erneut an.",

  "signin.title": "Anmelden",
  "signin.chooser.body": "Wählen Sie, als wer Sie sich anmelden.",
  "signin.chooser.loading": "Anmeldeoptionen werden geladen…",
  "signin.chooser.error": "Die Anmeldeoptionen konnten nicht geladen werden.",
  "signin.useEmail": "Stattdessen mit E-Mail anmelden",
  "signin.email.body": "Wir senden Ihnen einen Einmalcode per E-Mail.",
  "signin.email.field": "E-Mail",
  "signin.email.send": "Code senden",
  "signin.email.sent":
    "Wenn diese Adresse hinterlegt ist, ist ein Code unterwegs. Er läuft in {n} Minuten ab.",
  "signin.email.code": "Code eingeben",
  "signin.email.submit": "Anmelden",
  "signin.email.resend": "Neuen Code senden",
  "signin.email.wait": "In {n} s können Sie einen neuen Code anfordern.",
  "signin.email.other": "Andere Adresse verwenden",
  "signin.error.generic": "Die Anmeldung hat nicht funktioniert.",
  "signin.error.code": "Dieser Code stimmt nicht oder ist abgelaufen. Fordern Sie einen neuen an.",
  "signin.error.unavailable": "Diese Anmeldung ist nicht verfügbar. Beginnen Sie neu.",
  "signin.error.tooMany": "Zu viele Versuche. Fordern Sie einen neuen Code an.",

  "myVisits.title": "Meinen Besuch finden",
  "myVisits.eyebrow": "SMS verloren?",
  "myVisits.body":
    "Finden Sie alles, was Sie als Gast gebucht oder betreten haben — mit der verwendeten Telefonnummer.",
  "myVisits.field": "Mobilnummer",
  "myVisits.submit": "Besuche per SMS senden",

  "flow.join-queue.title": "In die Warteschlange",
  "flow.join-queue.cta": "In die Warteschlange",
  "flow.book-appointment.title": "Zeit buchen",
  "flow.book-appointment.cta": "Buchen",
  "flow.register-event.title": "Anmelden",
  "flow.register-event.cta": "Anmelden",
  "flow.unconfigured": "Dieser Block ist noch auf nichts ausgerichtet.",

  "faq.show": "Anzeigen",
  "faq.hide": "Ausblenden",

  "notice.dismiss": "Ausblenden",

  "cta.working": "Einen Moment…",
  "cta.retry": "Erneut versuchen",
  "cta.error": "Das hat nicht funktioniert.",
  "cta.pending.join": "Das Anstellen kommt mit der nächsten Lieferung",
  "cta.pending.book": "Das Buchen kommt mit der nächsten Lieferung",
  "cta.pending.register": "Das Anmelden kommt mit der nächsten Lieferung",
  "cta.pending.lookup": "Die Besuchssuche kommt mit der nächsten Lieferung",
  "cta.pending.signIn": "Die Anmeldung kommt mit der nächsten Lieferung",
  "cta.preview": "In der Vorschau nicht aktiv",
  "cta.offline": "Der Server war nicht erreichbar.",

  "visitor.legend": "Ihre Angaben",
  "visitor.firstName": "Vorname",
  "visitor.lastName": "Nachname",
  "visitor.email": "E-Mail",
  "visitor.phone": "Mobilnummer",
  "visitor.phoneWhy": "Damit wir Ihnen schreiben können, wenn Sie an der Reihe sind.",
  "visitor.optional": "Optional",
  "visitor.incomplete": "Ergänzen Sie Name und E-Mail, um fortzufahren.",
  "visitor.incompleteName": "Ergänzen Sie Ihren Namen, um fortzufahren.",
  "visitor.error.firstName": "Geben Sie Ihren Vornamen ein.",
  "visitor.error.lastName": "Geben Sie Ihren Nachnamen ein.",
  "visitor.error.emailRequired": "Geben Sie Ihre E-Mail-Adresse ein.",
  "visitor.error.email": "Das sieht nicht wie eine E-Mail-Adresse aus.",
  "visitor.error.phone": "Das sieht nicht wie eine Telefonnummer aus.",
  "visitor.cancel": "Abbrechen",

  "queue.joined.eyebrow": "Sie sind in der Warteschlange",
  "queue.joined.ticket": "Ticket {ref}",
  "queue.joined.ahead": "vor Ihnen",
  "queue.joined.wait": "Etwa {n} Min.",
  "queue.joined.code": "Check-in-Code {code}",
  "queue.already": "Sie sind bereits in dieser Warteschlange.",
  "queue.closed": "Dieser Schalter hat geschlossen.",
  "queue.leave": "Warteschlange verlassen",
  "book.hold.eyebrow": "Zeit für Sie reserviert",
  "book.holdUntil": "Reserviert bis {t}",
  "book.holdLeft": "{t} übrig",
  "book.confirm": "Diese Zeit bestätigen",
  "book.confirmed": "Termin bestätigt",
  "book.taken": "Diese Zeit wurde gerade vergeben.",
  "book.expired": "Ihre Reservierung ist abgelaufen.",
  "book.maybeBooked": "Ihre Buchung liegt vielleicht schon vor — prüfen Sie Ihre E-Mails.",
  "book.pickAnother": "Andere Zeit wählen",
  "book.otherTimes": "Andere Zeiten an diesem Tag",
  "book.noOtherTimes": "An diesem Tag ist sonst nichts frei.",
  "event.registered": "Sie sind angemeldet",
  ref: "Referenz {ref}",
  "ref.manage.aria": "Buchung {ref} verwalten (wird in neuem Tab geöffnet)",
  "copy.action": "Kopieren",
  "copy.done": "Kopiert",
  "copy.ticket": "Ticket {ref} kopieren",
  "copy.code": "Check-in-Code {code} kopieren",
  "copy.ref": "Referenz {ref} kopieren",

  "handoff.title": "Weiter zur Buchung",
  "handoff.body.session": "Ihre Buchung öffnet sich in einem neuen Tab, angemeldet als Sie. Diese Seite bleibt offen.",
  "handoff.body.guest": "Ihre Buchung öffnet sich in einem neuen Tab. Diese Seite bleibt offen.",
  "handoff.continue": "Weiter zur Buchung",
  "handoff.preparing": "Ihre Buchung wird vorbereitet…",
  "handoff.opened": "Ihre Buchung ist im anderen Tab offen.",
  "handoff.expired": "Dieser Buchungslink ist abgelaufen, bevor er genutzt wurde.",
  "handoff.new": "Neuen Buchungslink anfordern",
  "handoff.error": "Die Buchung ist im Moment nicht verfügbar.",
  "handoff.error.wait": "Zu viele Versuche. Bitte in {n}s erneut versuchen.",

  "block.unsupported.title": "Nicht unterstützter Block",
  "block.unsupported.body":
    "Für diesen Blocktyp gibt es keine Darstellung, er erscheint auf der veröffentlichten Seite nicht.",
};

/**
 * ui-config — de-CH DELTAS over `de`. Only the keys whose German spelling
 * differs in Swiss usage: «…» instead of „…“, and `ss` for `ß`. Everything else
 * resolves through `de`.
 */
export const DE_CH: LocaleCatalog = {
  "search.results.one": "{n} Ergebnis für «{q}»",
  "search.results.other": "{n} Ergebnisse für «{q}»",
  "search.noMatch.title": "Nichts passt zu «{q}»",
  "search.noMatch.body": "Versuchen Sie die Wörter aus Ihren Unterlagen, oder starten Sie hier:",
  "queues.walk": "{n} Min. zu Fuss",
};

/** ui-config — fr. Quotes « … » with the French spacing; `one` also covers 0. */
export const FR: LocaleCatalog = {
  "page.unavailable": "Cette page n’est pas disponible pour le moment.",
  "page.unavailable.body": "Merci de réessayer dans un instant.",

  "search.placeholder": "Dites-le avec vos propres mots…",
  "search.findHelp": "Trouver de l’aide",
  "search.clear": "Effacer la recherche",
  "search.clearShort": "Effacer",
  "search.aria": "Rechercher de l’aide",
  "search.results.one": "{n} résultat pour « {q} »",
  "search.results.other": "{n} résultats pour « {q} »",
  "search.results.sub": "Venez sans rendez-vous ou réservez un horaire — au plus rapide.",
  "search.noMatch.title": "Rien ne correspond à « {q} »",
  "search.noMatch.body": "Essayez les mots de vos documents, ou partez de l’un de ceux-ci :",
  "search.error": "La recherche n’est pas disponible pour le moment.",

  "busy.short-wait": "Attente courte",
  "busy.busy": "Affluence",
  "busy.very-busy": "Attente longue",
  "busy.closed": "Fermé",

  "queues.title": "Ouvert maintenant",
  "queues.subtitle": "Venez sans rendez-vous — voici les attentes actuelles.",
  "queues.empty": "Les temps d’attente ne sont pas disponibles pour le moment.",
  "queues.join": "Prendre la file",
  "queues.book": "Réserver un horaire",
  "queues.minutes": "min",
  "queues.hours": "h",
  "queues.walk": "{n} min à pied",
  "queues.openUntil": "Ouvert jusqu’à {t}",
  "queues.ahead": "{n} devant vous",
  "queues.more.one": "{n} guichet de plus dans ce périmètre.",
  "queues.more.other": "{n} guichets de plus dans ce périmètre.",
  "queues.sort.shortest-wait": "Attente la plus courte d’abord",
  "queues.sort.name": "Par nom",
  "queues.sort.location": "Par site",
  "queues.sort.curated": "Dans l’ordre défini ici",

  "appointments.title": "Vous préférez ne pas attendre ? Réservez un horaire",
  "appointments.subtitle": "Les prochaines disponibilités de la semaine.",
  "appointments.empty": "Aucun horaire réservable pour le moment.",
  "appointments.cta": "Réserver",
  "appointments.none": "Rien de libre sur cette période",
  "appointments.more.one": "{n} prestation de plus dans ce périmètre.",
  "appointments.more.other": "{n} prestations de plus dans ce périmètre.",

  "events.title": "Cette semaine",
  "events.subtitle": "Ateliers, salons et permanences de ces jours-ci.",
  "events.empty": "Rien n’est prévu sur cette période.",
  "events.featured.eyebrow": "À la une",
  "events.featured.title": "À ne pas manquer",
  "events.featured.empty": "Aucun événement n’est mis en avant pour le moment.",
  "events.all": "Tous les événements",
  "events.cta": "S’inscrire",
  "events.more.one": "{n} événement de plus dans ce périmètre.",
  "events.more.other": "{n} événements de plus dans ce périmètre.",

  "reg.open": "Inscriptions ouvertes",
  "reg.closed": "Inscriptions closes",
  "reg.full": "Complet",
  "reg.waitlist": "Liste d’attente",
  "reg.waitlist.count": "Liste d’attente — {n} en attente",
  "reg.not-required": "Aucune inscription nécessaire",
  "reg.counts": "{label} ({a}/{b})",
  "reg.countsOnly": "{label} ({a} inscrits)",

  "directory.title": "Parcourir les départements",
  "directory.titleCount": "Parcourir les {n} départements",
  "directory.subtitle":
    "Si vous savez déjà à qui vous adresser : chaque département, ce qu’il propose et comment.",
  "directory.empty": "La liste des départements n’est pas disponible pour le moment.",
  "directory.show": "Afficher",
  "directory.hide": "Masquer",
  "directory.inside.one": "{n} à l’intérieur",
  "directory.inside.other": "{n} à l’intérieur",
  "directory.cap.walkIns": "Sans rendez-vous",
  "directory.cap.books": "Réserver",
  "directory.cap.events": "Événements",
  "directory.more": "{n} de plus dans cette partie de l’organisation.",

  "checklist.title": "Cette semaine",
  "checklist.empty": "Vous êtes à jour cette semaine.",
  "checklist.more": "Afficher les {n}",
  "checklist.less": "Afficher moins",
  "checklist.cta.join-queue": "Sans rendez-vous",
  "checklist.cta.book-appointment": "Réserver",
  "checklist.cta.register-event": "S’inscrire",

  "identity.eyebrow": "En ce moment",
  "identity.signedOut.title": "Connectez-vous à votre compte",
  "identity.signedOut.button": "Continuer vers la connexion",
  "identity.findVisit": "Déjà dans une file ? Retrouver ma visite →",
  "identity.greeting": "Bon retour, {name}",
  "identity.greetingGeneric": "Bon retour",
  "identity.ahead": "devant vous",
  "identity.ticketCta": "Voir mon ticket",
  "identity.visitsCta": "Mes visites",
  "identity.degraded": "Certaines informations ne sont pas disponibles pour le moment.",
  "identity.nextAppointment": "Prochain rendez-vous",
  "identity.ticket.waiting": "Dans la file à {queue}",
  "identity.ticket.called": "Vous avez été appelé à {queue}",
  "identity.ticket.being-served": "Vous êtes reçu à {queue}",
  "identity.signOut": "Se déconnecter",
  "identity.demo": "Démo",
  "identity.sessionEnded": "Votre session est terminée. Merci de vous reconnecter.",

  "signin.title": "Connexion",
  "signin.chooser.body": "Choisissez avec quel profil vous vous connectez.",
  "signin.chooser.loading": "Chargement des options de connexion…",
  "signin.chooser.error": "Nous n’avons pas pu charger les options de connexion.",
  "signin.useEmail": "Se connecter avec son e-mail",
  "signin.email.body": "Nous vous enverrons un code à usage unique par e-mail.",
  "signin.email.field": "E-mail",
  "signin.email.send": "M’envoyer un code",
  "signin.email.sent":
    "Si cette adresse est enregistrée, un code est en route. Il expire dans {n} minutes.",
  "signin.email.code": "Saisissez le code",
  "signin.email.submit": "Se connecter",
  "signin.email.resend": "Envoyer un autre code",
  "signin.email.wait": "Vous pourrez demander un autre code dans {n} s.",
  "signin.email.other": "Utiliser une autre adresse",
  "signin.error.generic": "La connexion n’a pas abouti.",
  "signin.error.code": "Ce code est incorrect ou expiré. Demandez-en un nouveau.",
  "signin.error.unavailable": "Cette connexion n’est pas disponible. Recommencez.",
  "signin.error.tooMany": "Trop de tentatives. Demandez un nouveau code.",

  "myVisits.title": "Retrouver ma visite",
  "myVisits.eyebrow": "SMS perdu ?",
  "myVisits.body":
    "Retrouvez ce que vous avez réservé ou rejoint en tant qu’invité, avec le numéro utilisé.",
  "myVisits.field": "Numéro de mobile",
  "myVisits.submit": "M’envoyer mes visites par SMS",

  "flow.join-queue.title": "Prendre la file",
  "flow.join-queue.cta": "Prendre la file",
  "flow.book-appointment.title": "Réserver un horaire",
  "flow.book-appointment.cta": "Réserver",
  "flow.register-event.title": "S’inscrire",
  "flow.register-event.cta": "S’inscrire",
  "flow.unconfigured": "Ce bloc ne pointe encore vers rien.",

  "faq.show": "Afficher",
  "faq.hide": "Masquer",

  "notice.dismiss": "Fermer",

  "cta.working": "Un instant…",
  "cta.retry": "Réessayer",
  "cta.error": "Cela n’a pas fonctionné.",
  "cta.pending.join": "La prise de file arrive à la prochaine livraison",
  "cta.pending.book": "La réservation arrive à la prochaine livraison",
  "cta.pending.register": "L’inscription arrive à la prochaine livraison",
  "cta.pending.lookup": "La recherche de visite arrive à la prochaine livraison",
  "cta.pending.signIn": "La connexion arrive à la prochaine livraison",
  "cta.preview": "Inactif dans l’aperçu",
  "cta.offline": "Le serveur est injoignable.",

  "visitor.legend": "Vos informations",
  "visitor.firstName": "Prénom",
  "visitor.lastName": "Nom",
  "visitor.email": "E-mail",
  "visitor.phone": "Numéro de mobile",
  "visitor.phoneWhy": "Pour vous prévenir par SMS quand c’est votre tour.",
  "visitor.optional": "Facultatif",
  "visitor.incomplete": "Ajoutez votre nom et votre e-mail pour continuer.",
  "visitor.incompleteName": "Ajoutez votre nom pour continuer.",
  "visitor.error.firstName": "Saisissez votre prénom.",
  "visitor.error.lastName": "Saisissez votre nom.",
  "visitor.error.emailRequired": "Saisissez votre adresse e-mail.",
  "visitor.error.email": "Cela ne ressemble pas à une adresse e-mail.",
  "visitor.error.phone": "Cela ne ressemble pas à un numéro de téléphone.",
  "visitor.cancel": "Annuler",

  "queue.joined.eyebrow": "Vous êtes dans la file",
  "queue.joined.ticket": "Ticket {ref}",
  "queue.joined.ahead": "devant vous",
  "queue.joined.wait": "Environ {n} min",
  "queue.joined.code": "Code d’enregistrement {code}",
  "queue.already": "Vous êtes déjà dans cette file.",
  "queue.closed": "Ce guichet est fermé.",
  "queue.leave": "Quitter la file",
  "book.hold.eyebrow": "Horaire réservé pour vous",
  "book.holdUntil": "Réservé jusqu’à {t}",
  "book.holdLeft": "{t} restantes",
  "book.confirm": "Confirmer cet horaire",
  "book.confirmed": "Rendez-vous confirmé",
  "book.taken": "Cet horaire vient d’être pris.",
  "book.expired": "Votre réservation a expiré.",
  "book.maybeBooked": "Votre réservation est peut-être déjà enregistrée — vérifiez vos e-mails.",
  "book.pickAnother": "Choisir un autre horaire",
  "book.otherTimes": "Autres horaires ce jour-là",
  "book.noOtherTimes": "Rien d’autre n’est libre ce jour-là.",
  "event.registered": "Vous êtes inscrit",
  ref: "Référence {ref}",
  "ref.manage.aria": "Gérer la réservation {ref} (s’ouvre dans un nouvel onglet)",
  "copy.action": "Copier",
  "copy.done": "Copié",
  "copy.ticket": "Copier le ticket {ref}",
  "copy.code": "Copier le code d’enregistrement {code}",
  "copy.ref": "Copier la référence {ref}",

  "handoff.title": "Continuer vers la réservation",
  "handoff.body.session": "Votre réservation s’ouvre dans un nouvel onglet, avec votre session. Cette page reste ouverte.",
  "handoff.body.guest": "Votre réservation s’ouvre dans un nouvel onglet. Cette page reste ouverte.",
  "handoff.continue": "Continuer vers la réservation",
  "handoff.preparing": "Préparation de votre réservation…",
  "handoff.opened": "Votre réservation est ouverte dans l’autre onglet.",
  "handoff.expired": "Ce lien de réservation a expiré avant d’être utilisé.",
  "handoff.new": "Obtenir un nouveau lien",
  "handoff.error": "La réservation n’est pas disponible pour le moment.",
  "handoff.error.wait": "Trop de tentatives. Réessayez dans {n}s.",

  "block.unsupported.title": "Bloc non pris en charge",
  "block.unsupported.body":
    "Ce type de bloc n’a pas de rendu : il n’apparaîtra pas sur la page publiée.",
};

/** ui-config — it. Quotes «…» without inner spaces. */
export const IT: LocaleCatalog = {
  "page.unavailable": "Questa pagina non è disponibile in questo momento.",
  "page.unavailable.body": "La preghiamo di riprovare tra un momento.",

  "search.placeholder": "Lo dica con parole sue…",
  "search.findHelp": "Trova aiuto",
  "search.clear": "Cancella la ricerca",
  "search.clearShort": "Cancella",
  "search.aria": "Cerca aiuto",
  "search.results.one": "{n} risultato per «{q}»",
  "search.results.other": "{n} risultati per «{q}»",
  "search.results.sub": "Venga senza appuntamento o prenoti un orario: come fa prima.",
  "search.noMatch.title": "Nessun risultato per «{q}»",
  "search.noMatch.body": "Provi con le parole dei suoi documenti, oppure parta da qui:",
  "search.error": "La ricerca non è disponibile in questo momento.",

  "busy.short-wait": "Attesa breve",
  "busy.busy": "Molta affluenza",
  "busy.very-busy": "Attesa lunga",
  "busy.closed": "Chiuso",

  "queues.title": "Aperto adesso",
  "queues.subtitle": "Venga senza appuntamento: queste sono le attese in questo momento.",
  "queues.empty": "I tempi di attesa non sono disponibili in questo momento.",
  "queues.join": "Mettiti in coda",
  "queues.book": "Prenota un orario",
  "queues.minutes": "min",
  "queues.hours": "h",
  "queues.walk": "{n} min a piedi",
  "queues.openUntil": "Aperto fino alle {t}",
  "queues.ahead": "{n} davanti a lei",
  "queues.more.one": "{n} altro sportello in questo ambito.",
  "queues.more.other": "{n} altri sportelli in questo ambito.",
  "queues.sort.shortest-wait": "Prima l’attesa più breve",
  "queues.sort.name": "Per nome",
  "queues.sort.location": "Per sede",
  "queues.sort.curated": "Nell’ordine impostato qui",

  "appointments.title": "Preferisce non attendere? Prenoti un orario",
  "appointments.subtitle": "I prossimi orari liberi di questa settimana.",
  "appointments.empty": "Al momento non ci sono orari prenotabili.",
  "appointments.cta": "Prenota",
  "appointments.none": "Nessun orario libero in questo periodo",
  "appointments.more.one": "{n} altro servizio in questo ambito.",
  "appointments.more.other": "{n} altri servizi in questo ambito.",

  "events.title": "Questa settimana",
  "events.subtitle": "Workshop, fiere e sportelli aperti di questi giorni.",
  "events.empty": "In questo periodo non è previsto nulla.",
  "events.featured.eyebrow": "In evidenza",
  "events.featured.title": "Da non perdere",
  "events.featured.empty": "Al momento non c’è nessun evento in evidenza.",
  "events.all": "Tutti gli eventi",
  "events.cta": "Iscriviti",
  "events.more.one": "{n} altro evento in questo ambito.",
  "events.more.other": "{n} altri eventi in questo ambito.",

  "reg.open": "Iscrizioni aperte",
  "reg.closed": "Iscrizioni chiuse",
  "reg.full": "Completo",
  "reg.waitlist": "Lista d’attesa",
  "reg.waitlist.count": "Lista d’attesa — {n} in attesa",
  "reg.not-required": "Non serve iscriversi",
  "reg.counts": "{label} ({a}/{b})",
  "reg.countsOnly": "{label} ({a} iscritti)",

  "directory.title": "Sfoglia i reparti",
  "directory.titleCount": "Sfoglia tutti i {n} reparti",
  "directory.subtitle":
    "Se sa già a chi rivolgersi: ogni reparto, cosa prenota e come.",
  "directory.empty": "L’elenco dei reparti non è disponibile in questo momento.",
  "directory.show": "Mostra",
  "directory.hide": "Nascondi",
  "directory.inside.one": "{n} all’interno",
  "directory.inside.other": "{n} all’interno",
  "directory.cap.walkIns": "Senza appuntamento",
  "directory.cap.books": "Prenota",
  "directory.cap.events": "Eventi",
  "directory.more": "{n} altri in questa parte dell’organizzazione.",

  "checklist.title": "Questa settimana",
  "checklist.empty": "Questa settimana è tutto a posto.",
  "checklist.more": "Mostra tutti i {n}",
  "checklist.less": "Mostra meno",
  "checklist.cta.join-queue": "Senza appuntamento",
  "checklist.cta.book-appointment": "Prenota",
  "checklist.cta.register-event": "Iscriviti",

  "identity.eyebrow": "In questo momento",
  "identity.signedOut.title": "Acceda per vedere il suo conto",
  "identity.signedOut.button": "Continua e accedi",
  "identity.findVisit": "È già in coda? Trova la sua visita →",
  "identity.greeting": "Bentornata/o, {name}",
  "identity.greetingGeneric": "Bentornata/o",
  "identity.ahead": "davanti a lei",
  "identity.ticketCta": "Vedi il mio numero",
  "identity.visitsCta": "Le mie visite",
  "identity.degraded": "Alcuni dati non sono disponibili in questo momento.",
  "identity.nextAppointment": "Prossimo appuntamento",
  "identity.ticket.waiting": "In coda a {queue}",
  "identity.ticket.called": "È stata/o chiamata/o a {queue}",
  "identity.ticket.being-served": "La stiamo servendo a {queue}",
  "identity.signOut": "Esci",
  "identity.demo": "Demo",
  "identity.sessionEnded": "La sua sessione è terminata. Acceda di nuovo.",

  "signin.title": "Accedi",
  "signin.chooser.body": "Scelga con quale profilo accedere.",
  "signin.chooser.loading": "Caricamento delle opzioni di accesso…",
  "signin.chooser.error": "Non è stato possibile caricare le opzioni di accesso.",
  "signin.useEmail": "Accedi con l’e-mail",
  "signin.email.body": "Le invieremo un codice monouso per e-mail.",
  "signin.email.field": "E-mail",
  "signin.email.send": "Inviami un codice",
  "signin.email.sent":
    "Se l’indirizzo è registrato, il codice è in arrivo. Scade tra {n} minuti.",
  "signin.email.code": "Inserisca il codice",
  "signin.email.submit": "Accedi",
  "signin.email.resend": "Invia un altro codice",
  "signin.email.wait": "Potrà chiedere un altro codice tra {n} s.",
  "signin.email.other": "Usa un altro indirizzo",
  "signin.error.generic": "L’accesso non è andato a buon fine.",
  "signin.error.code": "Il codice non è corretto oppure è scaduto. Ne richieda uno nuovo.",
  "signin.error.unavailable": "Questo accesso non è disponibile. Ricominci.",
  "signin.error.tooMany": "Troppi tentativi. Richieda un nuovo codice.",

  "myVisits.title": "Trova la mia visita",
  "myVisits.eyebrow": "Ha perso l’SMS?",
  "myVisits.body":
    "Ritrovi ciò che ha prenotato o dove si è messa/o in coda come ospite, con il numero che ha usato.",
  "myVisits.field": "Numero di cellulare",
  "myVisits.submit": "Inviami le mie visite via SMS",

  "flow.join-queue.title": "Mettiti in coda",
  "flow.join-queue.cta": "Mettiti in coda",
  "flow.book-appointment.title": "Prenota un orario",
  "flow.book-appointment.cta": "Prenota",
  "flow.register-event.title": "Iscriviti",
  "flow.register-event.cta": "Iscriviti",
  "flow.unconfigured": "Questo blocco non punta ancora a nulla.",

  "faq.show": "Mostra",
  "faq.hide": "Nascondi",

  "notice.dismiss": "Chiudi",

  "cta.working": "Un momento…",
  "cta.retry": "Riprova",
  "cta.error": "Non è stato possibile completare l’operazione.",
  "cta.pending.join": "La coda arriva con la prossima consegna",
  "cta.pending.book": "La prenotazione arriva con la prossima consegna",
  "cta.pending.register": "L’iscrizione arriva con la prossima consegna",
  "cta.pending.lookup": "La ricerca della visita arriva con la prossima consegna",
  "cta.pending.signIn": "L’accesso arriva con la prossima consegna",
  "cta.preview": "Non attivo nell’anteprima",
  "cta.offline": "Non è stato possibile raggiungere il server.",

  "visitor.legend": "I suoi dati",
  "visitor.firstName": "Nome",
  "visitor.lastName": "Cognome",
  "visitor.email": "E-mail",
  "visitor.phone": "Numero di cellulare",
  "visitor.phoneWhy": "Così le scriviamo quando è il suo turno.",
  "visitor.optional": "Facoltativo",
  "visitor.incomplete": "Aggiunga nome ed e-mail per continuare.",
  "visitor.incompleteName": "Aggiunga il suo nome per continuare.",
  "visitor.error.firstName": "Inserisca il suo nome.",
  "visitor.error.lastName": "Inserisca il suo cognome.",
  "visitor.error.emailRequired": "Inserisca il suo indirizzo e-mail.",
  "visitor.error.email": "Non sembra un indirizzo e-mail.",
  "visitor.error.phone": "Non sembra un numero di telefono.",
  "visitor.cancel": "Annulla",

  "queue.joined.eyebrow": "È in coda",
  "queue.joined.ticket": "Numero {ref}",
  "queue.joined.ahead": "davanti a lei",
  "queue.joined.wait": "Circa {n} min",
  "queue.joined.code": "Codice di check-in {code}",
  "queue.already": "È già in questa coda.",
  "queue.closed": "Questo sportello è chiuso.",
  "queue.leave": "Esci dalla coda",
  "book.hold.eyebrow": "Orario riservato per lei",
  "book.holdUntil": "Riservato fino alle {t}",
  "book.holdLeft": "{t} rimasti",
  "book.confirm": "Conferma questo orario",
  "book.confirmed": "Appuntamento confermato",
  "book.taken": "Questo orario è stato appena occupato.",
  "book.expired": "La sua riserva è scaduta.",
  "book.maybeBooked": "Forse la sua prenotazione è già registrata: controlli le e-mail.",
  "book.pickAnother": "Scegli un altro orario",
  "book.otherTimes": "Altri orari di quel giorno",
  "book.noOtherTimes": "Quel giorno non è libero nient’altro.",
  "event.registered": "È iscritta/o",
  ref: "Riferimento {ref}",
  "ref.manage.aria": "Gestisci la prenotazione {ref} (si apre in una nuova scheda)",
  "copy.action": "Copia",
  "copy.done": "Copiato",
  "copy.ticket": "Copia il numero {ref}",
  "copy.code": "Copia il codice di check-in {code}",
  "copy.ref": "Copia il riferimento {ref}",

  "handoff.title": "Continua con la prenotazione",
  "handoff.body.session": "La prenotazione si apre in una nuova scheda, con la Sua sessione. Questa pagina resta aperta.",
  "handoff.body.guest": "La prenotazione si apre in una nuova scheda. Questa pagina resta aperta.",
  "handoff.continue": "Continua con la prenotazione",
  "handoff.preparing": "Stiamo preparando la Sua prenotazione…",
  "handoff.opened": "La Sua prenotazione è aperta nell’altra scheda.",
  "handoff.expired": "Questo link di prenotazione è scaduto prima di essere usato.",
  "handoff.new": "Richiedi un nuovo link",
  "handoff.error": "La prenotazione non è disponibile in questo momento.",
  "handoff.error.wait": "Troppi tentativi. Riprovi tra {n}s.",

  "block.unsupported.title": "Blocco non supportato",
  "block.unsupported.body":
    "Questo tipo di blocco non ha una resa: non apparirà sulla pagina pubblicata.",
};
